import { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { api } from '../../lib/api';
import { melbourneToday, weekOf } from '../../../shared/dates';
import { layOutWeek, weekStartsFrom, type CalendarItem, type CalendarHold, type CalendarKind, type DayEntry, type SpanSlot, type WeekLayoutMode } from '../../lib/weekLayout';
import { addDays, formatDayMonth, formatProjectNumber, formatWeekdayDate, Seg } from '../../components/primitives';
import { CallSheetsIcon, DueIcon, EditIcon, ShootIcon } from '../../components/icons';
import { useIsMobile } from '../../lib/useIsMobile';
import { agendaDateLabel, agendaFor, dayKinds } from '../../lib/weekStrip';
import { reportError } from '../../lib/errorEvents';
import { useNarrowCalendar } from './hooks';
import { HoldPanel, type HoldSelection } from './HoldPanel';
import { NewProjectModal } from '../Projects/NewProjectModal';

const WC_DAYS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
const WC_KIND_ICONS = { shoot: ShootIcon, edit: EditIcon, due: DueIcon };
const WC_KIND_LABELS: Record<CalendarKind, string> = {
  shoot: 'Shoot',
  edit: 'Edit',
  due: 'Delivery due',
  hold: 'Hold',
};
function wcTitle(item: CalendarItem): string {
  const range =
    item.end === item.start
      ? formatDayMonth(item.start)
      : `${formatDayMonth(item.start)} → ${formatDayMonth(item.end)}`;
  const num = item.kind !== 'hold' && item.project_number !== null ? `${formatProjectNumber(item.project_number)} ` : '';
  return `${WC_KIND_LABELS[item.kind]} · ${num}${item.name} · ${range}`;
}
function WcIcon({ kind }: { kind: CalendarKind }) {
  if (kind === 'hold') return <ShootIcon className="wc-ico hold" />;
  const Glyph = WC_KIND_ICONS[kind];
  return <Glyph className={`wc-ico ${kind}`} />;
}
/** 2F frame 4: a call sheet hold carries a small teal sheet glyph after its HOLD tag. */
function WcCallsheet({ hold }: { hold: CalendarHold }) {
  return hold.callsheet_url ? <span className="wc-cs" title="From a call sheet"><CallSheetsIcon /></span> : null;
}
/** One item in a day cell. In the narrow layout a multi-day period shows
    its position ("2/5") instead of drawing a bar. */
function WcEntry({ entry, onHold }: { entry: DayEntry; onHold: (hold: CalendarHold) => void }) {
  const { item, step } = entry;
  if (item.kind === 'hold') return <button type="button" className="wc-entry hold" title={wcTitle(item)} onClick={() => onHold(item)}>
    <span className="wc-hold-tag">HOLD</span><WcCallsheet hold={item} /><span className="name">{item.name}</span>
    {step && <span className="wc-step">{step.index}/{step.total}</span>}
  </button>;
  return (
    <Link className="wc-entry" to={`/projects/${item.project_id}`} title={wcTitle(item)}>
      <WcIcon kind={item.kind} />
      <span className="name">{item.name}</span>
      {step && (
        <span className="wc-step">
          {step.index}/{step.total}
        </span>
      )}
    </Link>
  );
}
/** One day's slice of a multi-day period. Every covered cell renders the
    same markup so the segments line up; only the first visible one shows
    its label (`hushed` hides the rest without collapsing their height). */
function WcSpan({ slot, onHold, onRemove }: { slot: SpanSlot; onHold: (hold: CalendarHold) => void; onRemove: (hold: CalendarHold) => void }) {
  const { item, first, open, last } = slot;
  const cls = ['wc-span', item.kind, first ? 'start' : 'hushed', first && open ? 'open' : '', last ? 'end' : '']
    .filter(Boolean)
    .join(' ');
  if (item.kind === 'hold') return <div className={cls}>
    <button type="button" className="wc-hold-open" title={wcTitle(item)} onClick={() => onHold(item)} tabIndex={first ? undefined : -1} aria-hidden={first ? undefined : true}>
      <span className="wc-hold-tag">HOLD</span><WcCallsheet hold={item} /><span className="name">{item.name}</span>
    </button>
    {first && <button type="button" className="wc-hold-x" aria-label={`Remove hold: ${item.name}`} onClick={() => onRemove(item)}>×</button>}
  </div>;
  return (
    <Link
      className={cls}
      to={`/projects/${item.project_id}`}
      title={wcTitle(item)}
      tabIndex={first ? undefined : -1}
      aria-hidden={first ? undefined : true}
    >
      <WcIcon kind={item.kind} />
      <span className="name">{item.name}</span>
    </Link>
  );
}
/** Mon–Sun row. Layout (lanes, clipping, the per-day cap) comes from
    lib/weekLayout; this only paints it. Anything past the cap sits behind
    the day's "+N more", which expands in place so every item stays a link. */
function WeekRow({
  weekStart,
  today,
  items,
  mode,
  loading,
  onAdd, onHold, onRemove, selection, holdPanel,
}: {
  weekStart: string;
  today: string;
  items: CalendarItem[];
  mode: WeekLayoutMode;
  loading: boolean;
  onAdd: (day: string) => void;
  onHold: (hold: CalendarHold, day: string) => void;
  onRemove: (hold: CalendarHold, day: string) => void;
  selection: HoldSelection | null;
  holdPanel: React.ReactNode;
}) {
  const [expanded, setExpanded] = useState<string | null>(null);

  return (
    <div className={`week-cal${loading ? ' loading' : ''}`}>
      {layOutWeek({ weekStart, items, today, mode }).map((cell) => {
        const dt = new Date(`${cell.day}T00:00:00`);
        const open = expanded === cell.day;
        const bare = cell.slots.length === 0 && cell.entries.length === 0 && cell.overflow.length === 0;
        return (
          <div
            className={`wc-day${selection?.day === cell.day ? ' adding' : ''}${cell.today ? ' today' : ''}${cell.weekend ? ' weekend' : ''}`}
            key={cell.day}
          >
            <div className="wc-head">
              <span>{WC_DAYS[dt.getDay()]}</span>
              <span className="num">{dt.getDate()}</span>
              <button type="button" className={`wc-add${selection?.day === cell.day ? ' on' : ''}`} aria-label={`Add hold on ${cell.day}`} onClick={() => onAdd(cell.day)}>+</button>
            </div>
            {selection?.day === cell.day && holdPanel}
            {cell.slots.map((slot, lane) =>
              slot ? (
                <WcSpan key={slot.item.id} slot={slot} onHold={(hold) => onHold(hold, cell.day)} onRemove={(hold) => onRemove(hold, cell.day)} />
              ) : (
                <div className="wc-span gap" key={`gap-${lane}`} aria-hidden="true">
                  <span className="wc-ico" />
                  <span className="num">&nbsp;</span>
                </div>
              ),
            )}
            {cell.entries.map((entry) => (
              <WcEntry key={entry.item.id} entry={entry} onHold={(hold) => onHold(hold, cell.day)} />
            ))}
            {open && cell.overflow.map((entry) => <WcEntry key={entry.item.id} entry={entry} onHold={(hold) => onHold(hold, cell.day)} />)}
            {cell.overflow.length > 0 && (
              <button
                type="button"
                className="wc-more"
                aria-expanded={open}
                title={cell.overflow.map((entry) => wcTitle(entry.item)).join('\n')}
                onClick={() => setExpanded(open ? null : cell.day)}
              >
                {open ? 'Show less' : `+${cell.overflow.length} more`}
              </button>
            )}
            {bare && !loading && <div className="wc-empty">—</div>}
          </div>
        );
      })}
    </div>
  );
}
/** Week calendar (week-one feedback §9): project shoot days, edit periods
    and delivery deadlines, one Mon–Sun week (or a fortnight) at a time.
    Fetched separately from the Overview payload so stepping through weeks
    costs one small query, not the whole page; the offset is component state
    only, so a reload always lands back on this week. Today comes from the
    client clock, never the payload — the Overview payload can be served
    from yesterday's localStorage cache on first paint. */
export function WeekCalendar() {
  const today = melbourneToday();
  const [offset, setOffset] = useState(0);
  const [weeks, setWeeks] = useState<'1' | '2'>('1');
  const [items, setItems] = useState<CalendarItem[] | null>(null);
  const [loading, setLoading] = useState(true);
  const narrow = useNarrowCalendar();
  const isMobile = useIsMobile();
  const [selection, setSelection] = useState<HoldSelection | null>(null);
  const [converting, setConverting] = useState<CalendarHold | null>(null);
  const [refresh, setRefresh] = useState(0);
  // Mobile only (§2.5): the strip cell filtering the agenda, and the
  // horizontal swipe that steps weeks.
  const [selected, setSelected] = useState<string | null>(null);
  const swipe = useRef<{ id: number; x: number; y: number } | null>(null);
  const stepped = useRef(false);

  const span = weeks === '2' ? 2 : 1;
  const from = addDays(weekOf(today).start, offset * 7);
  const to = addDays(from, span * 7 - 1);

  useEffect(() => { setSelection(null); }, [from, to]);

  // A selection can't survive the range that produced it.
  useEffect(() => {
    setSelected((day) => (day !== null && (day < from || day > to) ? null : day));
  }, [from, to]);

  useEffect(() => {
    let live = true;
    setLoading(true);
    api<{ items: CalendarItem[] }>(`/api/admin/overview/calendar?from=${from}&to=${to}`)
      .then((res) => {
        if (!live) return;
        setItems(res.items);
        setLoading(false);
      })
      .catch((error) => {
        if (!live) return;
        setItems([]);
        setLoading(false);
        reportError(error, 'Could not load the calendar.');
      });
    return () => {
      live = false;
    };
  }, [from, to, refresh]);

  // Prev/next week by swiping the strip: 40px of horizontal travel, vertical
  // gestures left to the page scroll. The click that ends the swipe is
  // swallowed so the finish cell doesn't also select itself.
  const stripSwipe = {
    onPointerDown(e: React.PointerEvent<HTMLDivElement>) {
      // A step re-keys the strip, so the click that would have cleared this
      // never arrives; reset it here or the next tap is swallowed (review).
      stepped.current = false;
      if (e.pointerType === 'mouse') return;
      swipe.current = { id: e.pointerId, x: e.clientX, y: e.clientY };
    },
    onPointerUp(e: React.PointerEvent<HTMLDivElement>) {
      const start = swipe.current;
      swipe.current = null;
      if (!start || start.id !== e.pointerId) return;
      const dx = e.clientX - start.x;
      const dy = e.clientY - start.y;
      if (Math.abs(dx) < 40 || Math.abs(dy) > Math.abs(dx)) return;
      stepped.current = true;
      setOffset((o) => o + (dx < 0 ? 1 : -1));
    },
    onPointerCancel() {
      swipe.current = null;
    },
    onClickCapture(e: React.MouseEvent<HTMLDivElement>) {
      if (!stepped.current) return;
      stepped.current = false;
      e.stopPropagation();
      e.preventDefault();
    },
  };

  const removeHold = async (hold: CalendarHold) => {
    await api(`/api/admin/holds/${hold.hold_id}`, { method: 'DELETE' });
    setItems((current) => current?.filter((item) => item.id !== hold.id) ?? null);
    setRefresh((value) => value + 1);
  };
  const holdPanel = selection && <HoldPanel key={`${selection.mode}-${selection.day}-${selection.mode === 'add' ? '' : selection.hold.id}`}
    selection={selection} mobile={isMobile} onClose={() => setSelection(null)} onSaved={() => setRefresh((value) => value + 1)}
    onRemove={removeHold} onConvert={(hold) => { setSelection(null); setConverting(hold); }}
    onConfirmRemove={() => { if (selection.mode !== 'add') setSelection({ ...selection, mode: 'remove' }); }} />;
  const conversionModal = converting && <NewProjectModal onClose={() => setConverting(null)} prefill={{
    name: converting.name, shoot_date: converting.start, shoot_end_date: converting.end === converting.start ? null : converting.end,
    client_text: converting.client_text, note: converting.note,
  }} onCreated={() => {
    // The project POST has completed successfully before this callback runs.
    // Cleanup failures close the create form, so retries cannot duplicate it.
    void removeHold(converting).catch((error) => reportError(new Error(
      `Project created, but the hold could not be removed. Remove it from the calendar. ${error instanceof Error ? error.message : ''}`.trim(),
    )));
  }} />;

  if (isMobile) {
    const agenda = agendaFor(items ?? [], from, to, selected);
    return (
      <div className="group">
        <div className="group-head wk-head">
          <span className="group-title">{offset === 0 ? 'The week ahead' : 'Calendar'}</span>
          <span className="group-count">{loading || items === null ? '·' : items.length || 'clear'}</span>
          <span className="wk-nav">
            {offset !== 0 && (
              <button type="button" className="wk-link" onClick={() => setOffset(0)}>
                Today
              </button>
            )}
            <button
              type="button"
              className="chip wk-chev"
              aria-label="Previous week"
              onClick={() => setOffset((o) => o - 1)}
            >
              ‹
            </button>
            <button
              type="button"
              className="chip wk-chev"
              aria-label="Next week"
              onClick={() => setOffset((o) => o + 1)}
            >
              ›
            </button>
            <button type="button" className="wk-link" onClick={() => setWeeks((w) => (w === '1' ? '2' : '1'))}>
              {weeks === '1' ? '2 weeks' : '1 week'}
            </button>
          </span>
        </div>
        <div className={`wk-body${loading ? ' loading' : ''}`}>
          {weekStartsFrom(from, span).map((weekStart) => (
            <div className="week-strip" key={weekStart} {...stripSwipe}>
              {Array.from({ length: 7 }, (_, i) => addDays(weekStart, i)).map((day) => {
                const dt = new Date(`${day}T00:00:00`);
                return (
                  <button
                    type="button"
                    key={day}
                    className={`wsd${day === today ? ' today' : ''}${selected === day ? ' sel' : ''}`}
                    aria-pressed={selected === day}
                    onClick={() => setSelected((s) => (s === day ? null : day))}
                  >
                    <span className="wd">{WC_DAYS[dt.getDay()]}</span>
                    <span className="dn">{dt.getDate()}</span>
                    <span className="ev">
                      {dayKinds(items ?? [], day).map((kind) => (
                        <WcIcon key={kind} kind={kind} />
                      ))}
                    </span>
                  </button>
                );
              })}
            </div>
          ))}
          <div className="ag-head"><span className="lbl">Agenda</span><button type="button" className="wk-link" onClick={() => setSelection({ mode: 'add', day: selected ?? (today >= from && today <= to ? today : from) })}>+ Add hold</button></div>
          <div className="agenda">
            {agenda.map((item) => item.kind === 'hold' ? (
              <button type="button" className="ag hold" key={item.id} title={wcTitle(item)} onClick={() => setSelection({ mode: 'details', day: item.start < from ? from : item.start, hold: item })}>
                <span className="w">{agendaDateLabel(item)}</span><span className="wc-hold-tag">HOLD</span><WcCallsheet hold={item} /><span className="name">{item.name}</span>{item.client_text && <span className="who">{item.client_text}</span>}
              </button>
            ) : (
              <Link className="ag" key={item.id} to={`/projects/${item.project_id}`} title={wcTitle(item)}>
                <span className="w">{agendaDateLabel(item)}</span>
                <WcIcon kind={item.kind} />
                <span className="name">{item.name}</span>
              </Link>
            ))}
            {agenda.length === 0 && !loading && (
              <div className="empty-note">
                {selected === null ? 'Nothing scheduled.' : 'Nothing scheduled that day.'}
              </div>
            )}
          </div>
        </div>
        {holdPanel}{conversionModal}
      </div>
    );
  }

  return (
    <div className="group">
      <div className="group-head">
        <span className="group-title">{offset === 0 ? 'The week ahead' : 'Calendar'}</span>
        <span className="group-count">{loading || items === null ? '·' : items.length || 'clear'}</span>
      </div>
      <div className="cal-toolbar">
        <span className="nav">
          <button className="chip" title="Previous week" onClick={() => setOffset((o) => o - 1)}>
            ‹
          </button>
          <button className="chip" disabled={offset === 0} onClick={() => setOffset(0)}>
            Today
          </button>
          <button className="chip" title="Next week" onClick={() => setOffset((o) => o + 1)}>
            ›
          </button>
        </span>
        <span className="range">
          {formatWeekdayDate(from)} – {formatWeekdayDate(to)}
        </span>
        <Seg<'1' | '2'>
          options={[
            { value: '1', label: 'Week' },
            { value: '2', label: '2 weeks' },
          ]}
          value={weeks}
          onChange={setWeeks}
        />
      </div>
      {weekStartsFrom(from, span).map((weekStart) => (
        <WeekRow
          key={weekStart}
          weekStart={weekStart}
          today={today}
          items={items ?? []}
          mode={narrow ? 'list' : 'bars'}
          loading={loading}
          selection={selection} holdPanel={holdPanel}
          onAdd={(day) => setSelection({ mode: 'add', day })}
          onHold={(hold, day) => setSelection({ mode: 'details', day, hold })}
          onRemove={(hold, day) => setSelection({ mode: 'remove', day, hold })}
        />
      ))}
      <div className="wc-legend">
        <span><span className="hold-swatch" />HOLD</span>
        <span>
          <WcIcon kind="shoot" />
          Shoot
        </span>
        <span>
          <WcIcon kind="edit" />
          Edit
        </span>
        <span>
          <WcIcon kind="due" />
          Delivery due
        </span>
      </div>
      {conversionModal}
    </div>
  );
}
